/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/button_send_pressed.svg";
import * as React from "react";
import {useContext, useEffect} from "react";
import {ToastModule} from "@intuitionrobotics/thunderstorm/frontend";
import {ICONS} from "@res/icons";
import {COLORS} from "@res/colors";
import {MessagingModule} from "@modules/MessagingModule";
import * as emotion from "emotion";

import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {AudioWavePlayer} from "../../widget/speech-audio/AudioWavePlayer";
import {RequestPatientResponse} from "./RequestPatientResponse";
import {LoadingBar} from "../../widget/LoadingBar";
import {_setInterval, currentTimeMillies} from "@intuitionrobotics/ts-common";
import {VGap} from "../../widget/VGap";
import {hideTooltip, showTooltipAtXY} from "../../widget/appTooltip";
import {FONTS} from "@res/fonts";
import {ContactConnection} from "@app/ir-q-app-common/types/db-contact";
import DragAndDrop from "../../../pages/widget/DragAndDrop";
import {UploadVideoThumbnailDialog} from "./UploadVideoThumbnailDialog";
import {DB_Patient} from "@app/app-shared/patient";
import {CCInbox} from "@app/ir-q-app-common/types/message-api";
import {DB_MessageStatus, MessageMap, MessageStatuses} from "@app/ir-q-app-common/types/message";
import {ChatsContext, ChatsContextType} from "./ChatsContext";
import {ErrorToast} from "../../../themes/toasts";
import {ContactsContext} from "./ContactsContext";
import {PatientsContext} from "../patients/PatientsContext";
import {FileStatus} from "@intuitionrobotics/file-upload/shared/modules/BaseUploaderModule";

type Props = {
    groupContactId?: string,
    chat?: CCInbox,
    isSendingToChat?: boolean,
    onChange?: (dataToSend: { text?: string, audioFile?: File, files?: File[] }) => void,
    onCustomThumbnailUploaded?: (thumbnailFile: File) => void,
    onRequireResponse?: (requireResponse: boolean) => void,
    isUploadingCustomThumbnail?: () => void,
    patient?: DB_Patient
}

const chatBarStyle = emotion.css(
    {
        position: "relative",
        backgroundColor: COLORS.color_gray_medium,
        padding: "20px 16px"
    }
);

const progressBarWrapper = emotion.css(
    {
        width: "100%"
    }
);

const chatInputStyle = emotion.css(
    {
        outline: "none",
        width: "-webkit-fill-available",
        borderRadius: "6px",
        borderStyle: "none",
        borderColor: "transparent",
        resize: "none",
        flex: 1,
        padding: 8,
        wordBreak: "break-word",
        whiteSpace: "initial",
        "::placeholder": {
            fontSize: 14,
            color: "#a4a6a8",
            fontStyle: "italic"
        }
    });

const button_send_pressed = __asset_0;

// Voice messages are recorded with the browser-native MediaRecorder API — no encoder
// dependency (we dropped the unmaintained mic-recorder-to-mp3 + lamejs). Care Center
// runs in Chrome, where MediaRecorder produces WebM/Opus; ir-q-messaging relays it
// untouched and the ElliQ device (Android MediaPlayer) plays Opus/WebM natively —
// verified end-to-end, no MP3 / server transcode needed.
const AUDIO_MIME = "audio/webm";
const AUDIO_FILE_NAME = "audio-from-cc.webm";
export const ChatBoxInput = (props: Props) => {
    const [requireResponse, setRequireResponse] = React.useState(false);
    const [text, setText] = React.useState("");
    const [isBlocked, setIsBlocked] = React.useState(false);
    const [timer, setTimer] = React.useState(0);
    const [file, setFile] = React.useState<File | undefined>();
    const [files, setFiles] = React.useState<File[]>([]);
    const [isRecording, setIsRecording] = React.useState(false);
    const [interval, setInterval] = React.useState<number | undefined>(undefined);
    const [thumbnail, setThumbnail] = React.useState<File | undefined>(undefined);
    const cCtx = React.useContext(ChatsContext);
    const {contacts} = React.useContext(ContactsContext);
    const {demo} = useContext(PatientsContext)

    const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
    const audioChunksRef = React.useRef<BlobPart[]>([]);
    const audioStreamRef = React.useRef<MediaStream | null>(null);

    useEffect(() => {
        navigator.mediaDevices.getUserMedia({audio: true}).then(() => {
            console.log("Permission Granted");
            setIsBlocked(false);
        }).catch(() => {
            console.log("Permission Denied");
            setIsBlocked(true);
        })
    }, []);

    const startCounter = () => {
        const interval = _setInterval(() => {
            setTimer(prevTimer => {

                if (prevTimer === 2 * 60) {
                    clearInterval(interval);
                    stopRecording();
                    return 0;
                }

                return prevTimer + 1;
            });
        }, 1000);

        setInterval(interval);
    }

    const startRecording = () => {
        if (isBlocked)
            return;

        hideTooltip()

        navigator.mediaDevices.getUserMedia({audio: true})
            .then(stream => {
                audioStreamRef.current = stream;
                audioChunksRef.current = [];
                const mimeType = MediaRecorder.isTypeSupported(AUDIO_MIME) ? AUDIO_MIME : undefined;
                const mr = new MediaRecorder(stream, mimeType ? {mimeType} : undefined);

                mr.ondataavailable = (e: BlobEvent) => {
                    if (e.data && e.data.size > 0)
                        audioChunksRef.current.push(e.data);
                };
                mr.onstop = () => {
                    const _file = new File(audioChunksRef.current, AUDIO_FILE_NAME, {
                        type: mr.mimeType || AUDIO_MIME,
                        lastModified: currentTimeMillies()
                    });
                    // release the mic
                    audioStreamRef.current?.getTracks().forEach(t => t.stop());
                    audioStreamRef.current = null;

                    setIsRecording(false);
                    setTimer(0);
                    setInterval(undefined);
                    setFile(_file);
                    props.onChange?.({audioFile: _file, text: undefined, files: undefined});
                };

                mediaRecorderRef.current = mr;
                mr.start();
                startCounter();
                setIsRecording(true);
            }).catch((e: Error) => {
            ToastModule.toastInfo("You need to grant microphone permission");
            console.error(e);
        });
    }

    const stopRecording = () => {
        // Stop the recorder; mr.onstop (set in startRecording) builds the audio File
        // from the captured chunks, releases the mic, and resets the recording state.
        clearInterval(interval);
        mediaRecorderRef.current?.stop();
        hideTooltip()
    };

    const renderAudioButtons = () => {
        if (isRecording) {
            return <div style={{display: "flex", borderRadius: "31.5px", justifyContent: "space-between", backgroundColor: "white"}}>
                <div style={{margin: 5, display: "flex", justifyContent: "center", alignItems: "center"}}>
                    {ICONS.microphone_recording(timer % 2 === 0 ? COLORS.black : "transparent", 24)}
                    <label className={chatInputStyle}>{renderTimeElapsed()}</label>
                </div>
                <div style={{cursor: "pointer", backgroundColor: "red", padding: "10px", margin: "8px", borderRadius: "100%", width: 12, height: 12}}
                     onMouseEnter={(e: React.MouseEvent) => {
                         const rect = e.currentTarget.getBoundingClientRect();
                         const x = rect.x - rect.width / 2 - 50;
                         const y = rect.y - rect.height - 50;
                         showTooltipAtXY(FONTS.reg_14_secondary_dark("The maximum voice recording length can be up to two minutes"), x, y,
                             {
                                 padding: 10,
                                 backgroundColor: COLORS.white,
                                 transform: "translate(-50%)",
                                 border: `1px solid ${COLORS.primary_light_blue}`,
                                 zIndex: 100,
                                 width: 200
                             });
                     }}
                     onMouseLeave={() => hideTooltip()}
                     onClick={() => stopRecording()}>
                    <div style={{backgroundColor: "white", width: "100%", height: "100%"}}/>
                </div>
            </div>
        }

        return <div style={{display: "flex", borderRadius: "31.5px", position: "relative", alignItems: "center", backgroundColor: "white"}}>
            <section className={chatInputStyle} style={{borderRadius: "31.5px"}}>
                {
                    // TODO: If I don't add the "!isRecording" clause, the previous AudioWavePlayer
                    // is not unmounted, so the audio file it has loaded is not updated. We need to investigate this further.
                    // blob prop has priority.
                    file && !isRecording && <AudioWavePlayer
                        trashAudio={() => {
                            setText("");
                            setFile(undefined)
                            setFiles(prevFiles => {
                                return prevFiles.filter(f => f.name !== AUDIO_FILE_NAME);
                            })
                            props.onChange?.({})
                        }}
                        url={""}
                        blob={file}
                        isSending={true}
                    />
                }
            </section>
            {renderSendIcon()}
        </div>
    };

    // _file is the entry being rendered - do not shadow the recorded audio "file" state,
    // it used to be reported back as the audioFile when removing an attachment.
    const renderFile = (_file: File, index: number) => {
        return <div key={`${_file.name}-${index}`} style={{display: "flex", margin: 8}}>
            {_file.name !== AUDIO_FILE_NAME && <div className={"clickable"} onClick={() => {
                setFiles(prevFiles => {
                    // remove by position - several attachments can share a name
                    const newFiles = prevFiles.filter((_, i) => i !== index);
                    props.onChange?.({text, audioFile: file, files: newFiles});
                    return newFiles;
                });
            }}>
                {ICONS.close(COLORS.red, 20)}
            </div>}
            {FONTS.medium_12(_file.name, {marginLeft: 10, color: COLORS.primary_dark_blue, alignSelf: "center"})}
        </div>;
    }

    const renderThumbnail = (_thumbnail: File) => {
        return <div style={{margin: 8}}>
            Thumbnail file
            <div style={{display: "flex", marginTop: 10}}>
                <div className={"clickable"} onClick={() => setThumbnail(undefined)}>
                    {ICONS.close(COLORS.red, 20)}
                </div>
                {FONTS.medium_12(_thumbnail.name, {marginLeft: 10, color: COLORS.primary_dark_blue, alignSelf: "center"})}
            </div>
        </div>;
    }


    const renderInput = () => {
        if (isRecording || file)
            return renderAudioButtons();

        return <div style={{display: "flex", borderRadius: "31.5px", position: "relative", alignItems: "center", backgroundColor: "white"}}>
			<textarea
                style={{borderRadius: "31.5px"}}
                value={text}
                placeholder={props.patient ? `Type a new message to ${props.patient.name}` : ""}
                className={chatInputStyle}
                maxLength={140}
                onChange={event => {
                    const message = event.target.value.substring(0, 140);
                    setText(message);
                    setFile(undefined);

                    props.onChange?.({text: message, audioFile: undefined, files: files});
                }}
                name={"new_chat"}
            />
            {text.length > 0 || file || files.length > 0 ? renderSendIcon() : renderMicIcon()}
        </div>
    };

    const renderMicIcon = () => {
        if (file || isRecording)
            return null

        return <div
            onMouseEnter={(e: React.MouseEvent) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = rect.x - rect.width / 2 - 50;
                const y = rect.y - rect.height - 50;
                showTooltipAtXY(FONTS.reg_14_secondary_dark("The maximum voice recording length can be up to two minutes"), x, y,
                    {
                        padding: 10,
                        backgroundColor: COLORS.white,
                        transform: "translate(-50%)",
                        border: `1px solid ${COLORS.primary_light_blue}`,
                        zIndex: 100,
                        width: 200
                    });
            }}
            onMouseLeave={() => hideTooltip()}
            style={{cursor: "pointer"}}
            onClick={() => startRecording()}>{ICONS.microphone("#53BAD5", 38)}</div>;
    };

    const renderSendIcon = () => {
        const patientContactId = props.patient?.contactId;
        if (!patientContactId || !props.chat)
            return null

        const patientContact = contacts[patientContactId];
        const canSendMessage = patientContact?.connections?.find((connection: ContactConnection) => connection._id === props.groupContactId);

        if (!canSendMessage)
            return ICONS.send(COLORS.grays_4, 44);

        const groupContactId = props.groupContactId;
        if (!groupContactId)
            return null;

        // Never mutate the files state here: renderSendIcon runs on every render, and
        // pushing the recorded audio into the state array appended another copy of it
        // each time (ChatsContext updates re-render this component constantly), so a
        // single recording ended up being uploaded and sent once per render - the
        // "duplication" of ELLIQ-24400 and the flood of messages on the unit.
        const uploadedFiles = file ? [...files, file] : files;
        if (uploadedFiles.length === 0 && !text?.trim())
            return ICONS.send(COLORS.grays_4, 44);

        return <img
            src={button_send_pressed}
            className={"clickable"}
            alt="send"
            height={44}
            width={44}
            onClick={() => {
                // a send is already in flight for this chat - a second click would
                // upload and send the same files again
                if (props.isSendingToChat)
                    return;

                if(demo) {
                    onSendMessage(true, {text: text.trim()}, cCtx, groupContactId, props.chat!)
                    setText("");
                    return;
                }

                const chat = props.chat!;
                AnalyticsModule.logCareManagerEvent({
                    objectType: AnalyticConsts.ObjectTypes.MESSAGES,
                    entity: AnalyticConsts.Entities.USER,
                    activityStatus: AnalyticConsts.ActivityStatus.CREATE,
                    objectSubType: AnalyticConsts.ObjectSubTypes.CHAT_BOX,
                    activityDetails: AnalyticConsts.ActivityDetails.SEND_MESSAGE,
                    objectId: chat._id
                });

                cCtx.setIsSendingToChat(chat._id, true);

                const data = {text: text.trim(), files: uploadedFiles};

                if (data.text && !data.files.length) {
                    MessagingModule.sendTextMessageFromUI(contacts, [groupContactId], [patientContactId], data.text, requireResponse)
                        .then(async (msg) => onSendMessage(true, data, cCtx, groupContactId, chat))
                        .catch(error => onSendMessage(false, data, cCtx, groupContactId, chat));
                } else if (data.files.length) {
                    MessagingModule.sendMediaMessageFromUI([groupContactId], [patientContactId], data, requireResponse,
                        async (feId, status) => {
                            // the upload failed, so no send will follow for this file - release the
                            // chat, otherwise the loading bar spins for ever and the input stays blocked
                            if (status === FileStatus.Error)
                                return onSendMessage(false, data, cCtx, groupContactId, chat);

                            const {uploadingDetails, doc} = MessagingModule.onFileStatusChanged(feId, status)
                            if (!doc || !uploadingDetails)
                                return

                            await MessagingModule.sendMediaOnBehalf(contacts, uploadingDetails, doc, cCtx,
                                (_data, mimeType, messages, _file) => onSendMessage(false, _data, cCtx, groupContactId, chat, mimeType, _file),
                                async (_data, mimeType, messages, _file) => onSendMessage(true, _data, cCtx, groupContactId, chat, mimeType, _file)
                            )
                        }, thumbnail).catch(console.error)
                }

                setText(uploadedFiles.length > 1 ? text : "");
                setFiles([]);
                setThumbnail(undefined);
                setFile(undefined);
                setRequireResponse(false)
            }}/>;
    };

    const onSendMessage = (success: boolean, data: MessageMap[keyof MessageMap], {
                               messages,
                               saveChats,
                               saveMessages,
                               setIsSendingToChat
                           }: ChatsContextType, groupContactId: string, chat: CCInbox,
                           mimeType = "text", file?: File) => {

        if (!success)
            ErrorToast("Error while sending message");

        setIsSendingToChat(chat._id, false);

        const message = MessagingModule.onSentMessageInExistingChat(chat, groupContactId, data, mimeType, file);
        const updateStatus: DB_MessageStatus = {...chat.lastMessage.status, status: success ? MessageStatuses.SENT : MessageStatuses.FAILED};
        chat.lastMessage = {...message, status: updateStatus};
        saveChats([chat])

        if (messages?.[chat._id]) {
            saveMessages([{...message, status: updateStatus}]);
        }
    }

    const renderTimeElapsed = () => {
        const minute = Math.floor(timer / 60);
        const counter = timer % 60;
        return `${(padLeft(minute))}:${(padLeft(counter))}`;
    };

    const padLeft = (num: number) => {
        return `${num < 10 ? "0" : ""}${num}`;
    };

    return <div className={chatBarStyle}>
        <DragAndDrop height={"100%"} onChange={(_files) => {
            const uploadedFiles = [...files, ..._files.accepted];
            if (uploadedFiles.length > 5) {
                ToastModule.toastError(`You cannot send more than 5 files at a time.`);
                return;
            }
            setFiles(uploadedFiles)

            props.onChange?.({text: text, audioFile: undefined, files: uploadedFiles});

            if (_files.accepted.some(f => f.name.endsWith(".mp4"))) {
                if (props.chat)
                    UploadVideoThumbnailDialog.show((_thumbnail) => setThumbnail(_thumbnail))
                else
                    props.isUploadingCustomThumbnail?.()
            }

        }} validate={["mp4",
            "mp3",
            "png",
            "jpg",
            "jpeg",
            "amr",
            "wav"]}>
            {
                props.isSendingToChat && <div
                    className={"absolute top left match_height match_width"}
                    style={{opacity: 0.1, backgroundColor: "black"}}
                />
            }
            <div className={`${progressBarWrapper}`} style={{marginBottom: 15}}>
                {props.isSendingToChat && <LoadingBar status={"loading"}/>}
            </div>
            {renderInput()}
            <VGap v={10}/>
            {files.map((_file, index) => renderFile(_file, index))}
            {thumbnail && renderThumbnail(thumbnail)}
            {<RequestPatientResponse
                disabled={props.isSendingToChat}
                requireResponse={requireResponse}
                onChange={_requireResponse => {
                    setRequireResponse(_requireResponse)

                    if (props.onRequireResponse)
                        props.onRequireResponse(_requireResponse)
                }}
                text="Ask for customer's feedback"
            />}
        </DragAndDrop>
    </div>
}
